// src/LoginPage.js
import React, { useState, useContext, useEffect } from "react";

import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Alert,
  Link,
  Modal,
  Snackbar,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../config/firebase";
import { SchoolContext } from "../../config/SchoolContext";
import { useAuth } from "../../config/AuthContext";
import MuiAlert from "@mui/material/Alert";
import splogo from "../../assets/splogo.png";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const navigate = useNavigate();
  const { setSchoolId } = useContext(SchoolContext);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      navigate("/Dashboard");
    }
  }, [currentUser, navigate]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setError("");
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Fetch school_id from Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const schoolId = userDoc.data().school_id;
        setSchoolId(schoolId); // Set school_id in context
        navigate("/Dashboard");
      } else {
        throw new Error("No such document!");
      }
    } catch (error) {
      console.error("Error logging in:", error);
      setError("Failed to log in. Please check your email and password");
    }
  };

  const handleResetPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      handleClose();
      setSnackbarOpen(true); // Open snackbar on success
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setError(
        "Failed to send password reset email. Please check the email address.",
      );
    }
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  return (
    <Container maxWidth="xs">
      <Box mt={8} display="flex" flexDirection="column" alignItems="center">
        {/* Logo positioned very close to login box */}
        <Box mb={0.25}>
          <img
            src={splogo}
            alt="Sinclair Logo"
            style={{
              height: "80px",
              width: "auto",
              display: "block",
            }}
          />
        </Box>

        <Card>
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Avatar sx={{ m: 1, bgcolor: "error.main" }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography variant="h5">Login</Typography>
              {error && (
                <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                  {error}
                </Alert>
              )}
              <form
                onSubmit={handleLogin}
                style={{ width: "100%", marginTop: "8px" }}
              >
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="error"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Sign In
                </Button>
              </form>
              <Link component="button" variant="body2" onClick={handleOpen}>
                Reset Password
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Reset Password
          </Typography>
          <TextField
            label="Email"
            fullWidth
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            margin="normal"
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
              {error}
            </Alert>
          )}
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="error"
          >
            Send Reset Email
          </Button>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Password reset email has been sent!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoginPage;
